import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, NgZone, OnDestroy, Output } from '@angular/core';
import { ToastService } from '@isophi/core-ui';
import { TranslocoService } from '@jsverse/transloco';

import { DictationError, DictationService, DictationSession, DictationState } from '../../services/dictation/dictation.service';

/** "Diktovat" next to a text editor: streams speech to Soniox and emits the recognised text (POR-001 §5). */
@Component({
  selector: 'app-dictation-button',
  templateUrl: './dictation-button.component.html',
  styleUrls: ['./dictation-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DictationButtonComponent implements OnDestroy {
  private readonly dictation = inject(DictationService);
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);

  /** Finalised text to append to the editor. */
  @Output() dictated = new EventEmitter<string>();

  protected readonly supported = this.dictation.supported;
  protected readonly configured = this.dictation.configured;

  protected state: DictationState = 'idle';
  protected partial = '';
  private session: DictationSession | null = null;

  protected async toggle(): Promise<void> {
    if (this.state === 'listening') {
      this.session?.stop();
      return;
    }
    if (this.state !== 'idle') return;
    this.session = await this.dictation.start({
      finalText: (text) => this.zone.run(() => this.dictated.emit(text)),
      partialText: (text) => this.update(() => (this.partial = text)),
      stateChange: (state) =>
        this.update(() => {
          this.state = state;
          if (state === 'idle') this.session = null;
        }),
      error: (error) => this.zone.run(() => this.showError(error)),
    });
  }

  ngOnDestroy(): void {
    this.session?.cancel();
  }

  private update(change: () => void): void {
    this.zone.run(() => {
      change();
      this.cdr.markForCheck();
    });
  }

  private showError(error: DictationError): void {
    this.toast.error(this.transloco.translate(`dictation/error/${error}`));
  }
}
